/**
 * HITL (human-in-the-loop) task API — shared by the Core Shell task inbox and
 * the in-chat gate card.
 *
 * A HITL task is the durable record of a run paused on a human decision —
 * raised by either engine (a LangGraph tool approval or a Temporal workflow
 * gate; `hitlType` says which). `contractId` is the resolve key; `render` is
 * the engine-authored payload the UI paints.
 *
 * Injected-client pattern (same as `conversations.js`): the host passes its
 * `apiClient` as the first argument, so this package pulls in no api singleton
 * and works in both core-shell and agentic-studio.
 *
 * Endpoints (Core `hitl` stack):
 *   POST /v1/tasks/hitl/list     — paginated, calling user's tasks only
 *   POST /v1/tasks/hitl/resolve  — submit the decision, resumes the run
 */

/**
 * Per-call axios timeout for `resolveHitlTask`. Resolving a gate round-trips
 * the engine resume (for Temporal, through Core → the workflow), which can
 * exceed the api-client's 10s default — the XHR would cancel before Core
 * responds, so the inbox slide-out and the chat gate card never learn the
 * gate resolved (observed live 2026-08-08 and 2026-08-10). The HTTP API
 * integration itself caps at 30s, so ~35s covers the real worst case while
 * still surfacing a genuine gateway timeout instead of an early client abort.
 */
export const HITL_RESOLVE_AXIOS_TIMEOUT_MS = 35_000;

/**
 * List the calling user's HITL tasks.
 *
 * @param {import('axios').AxiosInstance} apiClient
 * @param {{ status?: 'pending'|'resolved'|'timed_out'|'all', page?: number,
 *   pageSize?: number }} [params]
 * @returns {Promise<{ items: object[], meta: object }>}
 */
export const listHitlTasks = async (
  apiClient,
  { status = 'pending', page = 1, pageSize = 20 } = {},
) => {
  // The @aimsos/api-client interceptor already returns `response.data`, so the
  // awaited value IS the platform envelope — do not unwrap twice.
  const envelope =
    (await apiClient.post('/v1/tasks/hitl/list', {
      status,
      page,
      page_size: pageSize,
    })) ?? {};
  return {
    items: Array.isArray(envelope.body) ? envelope.body : [],
    meta: envelope.meta ?? {},
  };
};

/**
 * Classify a HITL resolve error by HTTP status and Core error_code so callers
 * can distinguish a stale-card dismiss from a retryable transport failure.
 *
 * Error map (Core `hitl` resolver):
 *  - 404 HITL_ERR_TASK_NOT_FOUND   → already resolved / gone
 *  - 409 HITL_ERR_TASK_NOT_PENDING → already resumed (clean race)
 *  - 502 HITL_ERR_RESUME_FAILED    → Temporal update failed (also-already-resumed
 *    race via temporal_hitl_client.py) — dismiss with softer copy
 *  - anything else                 → retryable (keep the card, show generic toast)
 *
 * The function is pure so it can be unit-tested without a network stub.
 *
 * @param {unknown} err
 * @returns {{ kind: 'already_resolved' | 'resume_conflict' | 'retryable',
 *   status: number | undefined, errorCode: string | undefined }}
 */
export const classifyHitlResolveError = (err) => {
  const status = err?.response?.status ?? err?.status;
  const errorCode = err?.response?.data?.error_code;

  if (
    status === 404 ||
    errorCode === 'HITL_ERR_TASK_NOT_FOUND' ||
    status === 409 ||
    errorCode === 'HITL_ERR_TASK_NOT_PENDING'
  ) {
    return { kind: 'already_resolved', status, errorCode };
  }

  if (status === 502 && errorCode === 'HITL_ERR_RESUME_FAILED') {
    return { kind: 'resume_conflict', status, errorCode };
  }

  return { kind: 'retryable', status, errorCode };
};

/**
 * Resolve one HITL task with the human decision.
 *
 * The decision's required shape depends on the gate — build it with the
 * helpers in `utils/hitlDecision.js`. Returns the resolve outcome unchanged
 * (a rejected decision is a SUCCESS carrying `error_code: 'hitl_rejected'`,
 * not a thrown error).
 *
 * @param {import('axios').AxiosInstance} apiClient
 * @param {{ contractId: string, decision: object }} params
 * @returns {Promise<object>}
 */
export const resolveHitlTask = async (apiClient, { contractId, decision }) => {
  // Interceptor-unwrapped: the awaited value is the resolve outcome itself.
  return (
    (await apiClient.post(
      '/v1/tasks/hitl/resolve',
      { contract_id: contractId, decision },
      { timeout: HITL_RESOLVE_AXIOS_TIMEOUT_MS },
    )) ?? {}
  );
};
